import { FC } from "react";
import styled from "styled-components";
import Colors from "../../../ui/global/Colors";
import Fonts from "../../../ui/global/Fonts";
import Icon from "../../../ui/global/Icon";
import Checkbox from "../../../ui/components/Checkbox";

const ButtonText = styled.div<{ $color: string; $size: number; $weight: number; }>`
    font-size: ${({ $size }) => $size}px;
    line-height: 1.2em;
    font-weight: ${({ $weight }) => $weight};
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    color: ${({ $color }) => $color};
    text-align: right;
    user-select: none;
`;

const LoaderContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const ProgressFill = styled.div<{ $progress: number; $progressColor?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: ${({ $progress }) => Math.min(Math.max($progress, 0), 1) * 100}%;
  background-color: ${({ $progressColor }) => $progressColor};
  transition: width 0.3s ease-out;
  z-index: 1;
`;

const ContentOverlay = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: inherit;
  width: 100%;
  height: 100%;
  flex-direction: inherit; // Respects vertical/horizontal orientation
`;

const EditBarButtonRoot = styled.button<{ $disabled: boolean; $orientation: 'horizontal' | 'vertical'; $isMobile?: boolean }>`
  box-sizing: border-box;
  background-color: ${Colors.transparent};
  border: 1px solid ${Colors.transparent};
  border-radius: 6px;
  min-width: fit-content;
  min-height: fit-content;
  width: auto;
  height: auto;
  overflow: hidden;
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;
  white-space: nowrap;
  padding: 0 5px;
  gap: 5px;
  cursor: pointer;
  flex-shrink: 0;
  transition: transform 0.2s ease-in-out, background-color 0.1s ease-in-out, color 0.1s ease-in-out, opacity 0.2s ease-in-out;
  transform: scale(1);
  
  &:hover {
    transform: scale(1.08);
  }

  &:active {
    transform: scale(1);
  }

  ${({ $disabled }) => $disabled && `
    opacity: 0.5;
    cursor: default;
    pointer-events: none;

    &:hover {
      transform: scale(1);
    }
  `}

  ${({ $orientation }) => $orientation === 'vertical' && `
    flex-direction: column;

    gap: 0;
    
    ${ButtonText} {
      text-align: center;
    }
  `}
`;

interface EditBarButtonProps {
    text: string;
    icon?: string;
    onClick: () => void;
    disabled?: boolean;
    orientation?: 'horizontal' | 'vertical';
    isMobile?: boolean;
    progress?: number; // 0 to 1
}

const PROGRESS_COLOR = Colors.grey2;

export const EditBarButton: FC<EditBarButtonProps> = ({ text, icon, onClick, disabled = false, orientation = 'horizontal', isMobile, progress }) => {
    const iconTextSize = 13; 
    const iconTextColor = Colors.grayMed;
    const showProgress = typeof progress === 'number';

    return (
        <EditBarButtonRoot onClick={disabled ? undefined : onClick} $disabled={disabled} $orientation={orientation} $isMobile={isMobile}>
            {showProgress && (
                <LoaderContainer>
                    <ProgressFill $progress={progress} $progressColor={PROGRESS_COLOR} />
                </LoaderContainer>
            )}
            <ContentOverlay>
                <ButtonText 
                    $color={iconTextColor} 
                    $size={iconTextSize} 
                    $weight={Fonts.lexendSemiLight.fontWeight}
                >
                    {text}
                </ButtonText>
                {icon && <Icon onClick={()=>{}} icon={icon} color={iconTextColor} size={iconTextSize*1.1} />}
            </ContentOverlay>
        </EditBarButtonRoot>
    );
};

interface EditBarCheckboxProps {
    text: string;
    checked: boolean;
    onClick: (checked: boolean) => void;
    disabled?: boolean;
    orientation?: 'horizontal' | 'vertical';
    isMobile?: boolean;
    progress?: number; // 0 to 1
}

export const EditBarCheckbox: FC<EditBarCheckboxProps> = ({ text, checked, onClick, disabled = false, orientation = 'horizontal', isMobile, progress }) => {
    const iconTextSize = 13; 
    const iconTextColor = Colors.grey6;
    const checkboxSize = iconTextSize * 1.3;
    const showProgress = typeof progress === 'number';

    const handleClick = () => {
        if (!disabled) {
            onClick(!checked);
        }
    };

    return (
        <EditBarButtonRoot onClick={handleClick} $disabled={disabled} $orientation={orientation} $isMobile={isMobile}>
            {showProgress && (
                <LoaderContainer>
                    <ProgressFill $progress={progress} $progressColor={PROGRESS_COLOR} />
                </LoaderContainer>
            )}
            <ContentOverlay>
                <ButtonText 
                    $color={iconTextColor} 
                    $size={iconTextSize} 
                    $weight={Fonts.lexendSemiLight.fontWeight}
                >
                    {text}
                </ButtonText>
                <Checkbox
                    checked={checked}
                    onChange={() => {}} 
                    size={checkboxSize} 
                />
            </ContentOverlay>
        </EditBarButtonRoot>
    );
};